import { useEffect, useState } from 'react';

import { useT } from '../i18n';
import { ROUTES } from '../lib/router';
import { onConsentChange, readConsent, writeConsent } from '../lib/consent';
import { isMetaConfigured, loadMetaPixel } from '../lib/meta';
import './ConsentBanner.css';

/**
 * The cookie notice.
 *
 * Deliberately NOT a modal. It does not trap focus, dim the page or block the
 * form. The reader can ignore it entirely and still get their analysis, because
 * measurement is our interest, not theirs, and holding the product hostage to it
 * would contradict everything else on the page.
 *
 * It renders only when there is a decision to make: no pixel id configured means
 * nothing to consent to, and a decision already stored means do not ask again.
 *
 * NAMESPACE: `cookie-notice`, never `consent` — that class belongs to the step 3
 * agreement checkbox. See ConsentBanner.css.
 */
export function ConsentBanner() {
  const t = useT();
  const [state, setState] = useState(() => readConsent());

  /* Reflects the footer's "ask me again" link, which resets the stored value. */
  useEffect(() => onConsentChange(setState), []);

  /*
   * Re-load on a granted state that arrives from anywhere: a fresh click here,
   * a returning visitor who agreed on an earlier visit, or another tab. Calling
   * loadMetaPixel more than once is a no-op.
   */
  useEffect(() => {
    if (state === 'granted') loadMetaPixel();
  }, [state]);

  if (!isMetaConfigured() || state !== 'unset') return null;

  return (
    <section className="cookie-notice" role="region" aria-label={t.consent.regionLabel}>
      <div className="cookie-notice__card">
        <p className="cookie-notice__body">{t.consent.body}</p>

        {/* Second row: the two choices at one end, the way out at the other. */}
        <div className="cookie-notice__row">
          <div className="cookie-notice__actions">
            <button type="button" className="btn btn--accent" onClick={() => writeConsent('granted')}>
              {t.consent.accept}
            </button>
            <button type="button" className="btn" onClick={() => writeConsent('denied')}>
              {t.consent.decline}
            </button>
          </div>
          {/*
            New tab, like the agreement checkbox's link on step 3: someone reading
            the policy from here has not made their choice yet, and navigating
            away would throw it and any half-filled form together.
          */}
          <a className="cookie-notice__link" href={ROUTES.privacy} target="_blank" rel="noopener noreferrer">
            {t.consent.link}
          </a>
        </div>
      </div>
    </section>
  );
}
